/**
 * 招待ページ - 招待内容表示
 */

import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import type { PublicInvitation } from "hooks/useInvitationApi";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { formatLongDate } from "utils/relative-time";

type InvitationContentProps = {
  invitation: PublicInvitation;
  token: string;
  /** ログイン中ユーザーのメールアドレス (未ログインなら null) */
  currentUserEmail: string | null;
  /** 招待を承認してテナントに参加する */
  onAccept: () => void;
  /** 承認処理中かどうか */
  accepting: boolean;
  /** 別アカウントでログインし直す (サインアウト) */
  onSignOut: () => void;
};

const roleLabels: Record<string, string> = {
  owner: "オーナー",
  admin: "管理者",
  member: "メンバー",
};

export const InvitationContent: FC<InvitationContentProps> = ({
  invitation,
  token,
  currentUserEmail,
  onAccept,
  accepting,
  onSignOut,
}) => {
  const navigate = useNavigate();
  const isLoggedIn = currentUserEmail !== null;
  const emailMatches =
    currentUserEmail?.toLowerCase() === invitation.email.toLowerCase();
  const formattedExpiry = formatLongDate(invitation.expiresAt);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-base-200 px-4">
      <div className="card w-full max-w-lg bg-base-100 shadow-xl mx-auto">
        <div className="card-body items-center text-center">
          <div className="text-primary text-5xl mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-16 h-16"
              role="img"
              aria-label="招待メールアイコン"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
              />
            </svg>
          </div>

          <h1 className="card-title text-2xl">招待されました</h1>

          <p className="text-lg text-base-content mt-2">
            <span className="font-bold text-primary">
              {invitation.tenantName}
            </span>{" "}
            への参加招待が届いています
          </p>

          <div className="divider" />

          <div className="w-full text-left space-y-2 bg-base-200 p-4 rounded-lg">
            <div className="flex justify-between">
              <span className="text-base-content/70">メールアドレス</span>
              <span className="font-medium">{invitation.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-base-content/70">ロール</span>
              <span className="font-medium">
                {roleLabels[invitation.role] || invitation.role}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-base-content/70">有効期限</span>
              <span className="font-medium">{formattedExpiry}</span>
            </div>
          </div>

          <div className="divider" />

          {!isLoggedIn && (
            <>
              <p className="text-base-content/70 text-sm mb-4">
                アカウントを作成して参加しましょう
              </p>

              <div className="card-actions w-full flex-col gap-2">
                <Button
                  type="button"
                  width="full"
                  onClick={() =>
                    navigate(
                      `/signup?email=${encodeURIComponent(invitation.email)}&token=${token}`,
                    )
                  }
                >
                  サインアップ
                </Button>
                <p className="text-sm text-base-content/70 mt-2">
                  既にアカウントをお持ちの方は <Link to="/login">ログイン</Link>{" "}
                  後、この招待リンクをもう一度開いてください
                </p>
              </div>
            </>
          )}

          {isLoggedIn && emailMatches && (
            <div className="card-actions w-full flex-col gap-2">
              <Button
                type="button"
                width="full"
                disabled={accepting}
                onClick={onAccept}
              >
                {accepting ? "参加処理中..." : "このテナントに参加する"}
              </Button>
            </div>
          )}

          {isLoggedIn && !emailMatches && (
            <div className="card-actions w-full flex-col gap-2">
              <div role="alert" className="alert alert-warning text-left">
                <span>
                  現在 <span className="font-medium">{currentUserEmail}</span>{" "}
                  でログインしています。この招待は{" "}
                  <span className="font-medium">{invitation.email}</span>{" "}
                  宛のため、招待先のアカウントでログインし直してください。
                </span>
              </div>
              <Button type="button" width="full" isOutline onClick={onSignOut}>
                別のアカウントでログイン
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
